import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import { registerReset } from "@/store/resetStore";

export interface IncomingRequest {
  id: string;
  userId?: string;
  text?: string;
  timestamp?: string | number;
  payload?: any;
  sessionId?: string;
  cname?: string;
  customerId?: string;
  queueId?: string | number;
  channel?: "WEBBOT" | "WHATSAPP";
}

interface RequestsState {
  pendingRequests: IncomingRequest[];
  acceptedChats: string[];
  queueFetched: boolean;
  acceptHandler?: (request: IncomingRequest) => Promise<void> | void;
}

interface RequestsActions {
  addRequest: (request: IncomingRequest) => void;
  removeRequest: (id: string) => void;
  removeAcceptedChat: (id: string) => void;
  setAcceptHandler: (
    handler: (request: IncomingRequest) => Promise<void> | void
  ) => void;
  clearPendingRequests: () => void;
  acceptRequest: (request: IncomingRequest) => Promise<void>;
  clearAll: () => void;
  setQueueFetched: (value: boolean) => void;
}

type RequestsStore = RequestsState & RequestsActions;

const initialState: RequestsState = {
  pendingRequests: [],
  acceptedChats: [],
  queueFetched: false,
  acceptHandler: undefined,
};

const useRequestsStore = create<RequestsStore>()(
  devtools(
    persist(
      (set, get) => {
        // REGISTER GLOBAL RESET
        registerReset(() => set(initialState));

        return {
          ...initialState,

          addRequest: (request) =>
            set((state) => ({
              pendingRequests: [...state.pendingRequests, request],
            })),

          removeRequest: (id) =>
            set((state) => ({
              pendingRequests: state.pendingRequests.filter(
                (r) => r.id !== id
              ),
            })),

          removeAcceptedChat: (id) =>
            set((state) => ({
              acceptedChats: state.acceptedChats.filter(
                (chatId) => chatId !== id
              ),
            })),

          setAcceptHandler: (handler) => {
            set({ acceptHandler: handler });
          },

          acceptRequest: async (request) => {
            const handler = get().acceptHandler;
            if (!handler) return;

            await handler(request);

            set((state) => ({
              pendingRequests: state.pendingRequests.filter(
                (r) => r.id !== request.id
              ),
              acceptedChats: request.id
                ? Array.from(new Set([request.id, ...state.acceptedChats]))
                : state.acceptedChats,
            }));
          },
          clearPendingRequests: () =>
            set(() => ({
              pendingRequests: [],
            })),

          clearAll: () => set(initialState),

          setQueueFetched: (value) =>
            set(() => ({
              queueFetched: value,
            })),
        };
      },
      {
        name: "request-storage",
      }
    ),
    { name: "requests-store" }
  )
);

export default useRequestsStore;
